import React from "react";

import { CollapseHeader, FormCollapse } from "components/Form";
import { FinanceSubModalSection } from "constants/settings";
import { useSectionHelper, SectionToggleState } from "hooks";
import {
  Label,
  OptionTogglerField,
  Switch,
  withNamespace,
} from "components/Form/FormikControls";
import { Grid } from "@material-ui/core";
import { Column, Row } from "components/Layout";
import Big from "big.js";
import { mapDefaultDeploymentTransitInsuranceToBusinessValues } from "lib/insuranceRates";
import { useFormikContext } from "formik";
import { useSelector } from "react-redux";
import { getDefaultBillingSettings, getIsSaleyardAdmin } from "selectors";
import { InsuranceApplicationOptions } from "constants/billing";

interface BusinessInsuranceSectionProps {
  sectionHelper: SectionToggleState;
  namespace: string;
}

interface FormikValues {
  [key: string]: any;
}

export const BusinessInsuranceSection: React.FC<
  BusinessInsuranceSectionProps
> = ({ sectionHelper, namespace }) => {
  const { isOpen, onToggle } = useSectionHelper(
    sectionHelper,
    FinanceSubModalSection.INSURANCE,
  );

  const header = <CollapseHeader title="Insurance" />;
  const formikProps = useFormikContext<FormikValues>();
  const saleyardAdmin = useSelector(getIsSaleyardAdmin);

  const billingSettings = useSelector(getDefaultBillingSettings);
  const transitInsuranceForSales = billingSettings?.transitInsuranceForSales;
  const debtorInsurance = billingSettings?.debtorInsurance;

  const getInsuranceLabel = (insuranceName: string, insuranceRate: number) => {
    const baseLabel = `${insuranceName} Insurance`;
    if (!insuranceRate || saleyardAdmin) {
      return baseLabel;
    }
    const displayedRate = Big(insuranceRate).mul(100).toLocaleString();
    return `${baseLabel} (${displayedRate}%)`;
  };

  const onChangeTransitInsurance = (value: any) => {
    const mappedValues =
      mapDefaultDeploymentTransitInsuranceToBusinessValues(value);
    formikProps.setValues({ ...formikProps.values, ...mappedValues });
  };

  return (
    <FormCollapse isOpen={isOpen} onToggle={onToggle} header={header}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={12}>
          <Column paddingHorizontal={1}>
            <Row>
              <Label>
                {getInsuranceLabel(
                  "Transit",
                  transitInsuranceForSales?.standardRate || 0,
                )}
              </Label>
            </Row>
            <OptionTogglerField
              options={InsuranceApplicationOptions}
              name="transitInsuranceApplicationDefault"
              onChangeExtra={onChangeTransitInsurance}
            />
          </Column>
        </Grid>
        <Grid item xs={12} md={12}>
          <Row justifyCenter>
            <Grid>
              <Switch
                label={getInsuranceLabel(
                  "Debtor",
                  debtorInsurance?.standardRate || 0,
                )}
                name={withNamespace(namespace, "hasDebtorInsurance")}
                disabled={debtorInsurance?.insuredDefinedByProvider}
                tooltip={
                  debtorInsurance?.insuredDefinedByProvider
                    ? "Insured status is set by the insurance provider"
                    : ""
                }
              />
            </Grid>
          </Row>
        </Grid>
      </Grid>
    </FormCollapse>
  );
};
